import { useAxios } from '@/hooks/useAxios';
import { getQueryKey, useGetUserKey } from '@/hooks/useQueryKeys';
import { useMutation, useQuery } from '@/utils/api';

// Allowed share-link lifetimes. Mirrors the backend's validated `ttl` enum
// (architecture.md §2): POST /share accepts only these values, defaulting to
// "7d" when omitted.
export const SHARE_TTL_OPTIONS = ['7d', '14d', '30d'] as const;
export type ShareTtl = (typeof SHARE_TTL_OPTIONS)[number];

// POST /share response. The raw token is returned exactly ONCE — the backend
// stores only its hash. The frontend composes `<origin>/s/<token>` from it and
// never persists it.
export interface CreateShareResponse {
  token: string;
  expires_at: number;
}

// GET /share row. Owner-scoped, sanitized — NO raw token, NO other users'
// records (backend filters by CreatedBy). `created_at` is RFC3339;
// `expires_at` is an absolute unix epoch. `id` is an opaque, NON-SECRET
// per-share handle (the hashed #share# key — preimage-resistant, safe to hold
// client-side and pass to DELETE /share); never render it to the user.
export interface ShareListItem {
  id: string;
  filename: string;
  created_at: string;
  expires_at: number;
  revoked: boolean;
}

/**
 * Mints an opaque share link for a file the caller can already read.
 *
 * POST /share `{ name, ttl?, praetorian? }` → `{ token, expires_at }`. The
 * caller composes `<window.location.origin>/s/<token>`; the token is the only
 * thing that ever leaves the server in the clear, and only on this one
 * response. `praetorian?` is set for internal-partition files so the backend
 * stores+resolves the correct (praetorian) S3 partition.
 */
export function useCreateShare(email?: string) {
  const axios = useAxios({ email });

  return useMutation({
    defaultErrorMessage: 'Failed to create share link',
    mutationFn: async (props: {
      name: string;
      ttl?: ShareTtl;
      praetorian?: boolean;
    }): Promise<CreateShareResponse> => {
      const res = await axios.post<CreateShareResponse>('/share', {
        name: props.name,
        ...(props.ttl ? { ttl: props.ttl } : {}),
        ...(props.praetorian ? { praetorian: true } : {}),
      });
      return res.data;
    },
  });
}

/**
 * Resolves a share token to a presigned download URL.
 *
 * GET /share/<token> → `200 { url }` (authorized member) | `404` (generic — the
 * backend reveals nothing on any negative branch). Disabled until a token is
 * present; never retries (a 404 is a final, intentional answer, not a transient
 * failure to retry).
 */
export function useResolveShare(token: string | undefined) {
  const axios = useAxios();

  return useQuery<CreateShareResolveResponse>({
    defaultErrorMessage: 'This link is unavailable',
    retry: 0,
    queryKey: getQueryKey.getShareResolve(token),
    queryFn: async () => {
      const res = await axios.get<CreateShareResolveResponse>(
        `/share/${encodeURIComponent(token ?? '')}`
      );
      return res.data;
    },
    enabled: Boolean(token),
  });
}

export interface CreateShareResolveResponse {
  url: string;
}

/**
 * Lists the current user's own share links.
 *
 * GET /share → `[{ filename, created_at, expires_at, revoked }]`. The backend
 * filters to the caller's own shares and strips raw tokens; the UI must never
 * render a token (there isn't one in the payload to render).
 */
export function useGetShares(email?: string, options?: { enabled?: boolean }) {
  const axios = useAxios({ email });
  // Append the active impersonation `friend` (or `email`) to the cache key so a
  // user's `/share` list never bleeds across impersonated identities. Mirrors
  // the project-wide pattern used by `useMy` (SHARES stays the key prefix, so
  // predicate/prefix invalidation still matches).
  const queryKey = useGetUserKey(getQueryKey.getShares(email));

  return useQuery<ShareListItem[]>({
    defaultErrorMessage: 'Failed to load share links',
    queryKey,
    queryFn: async () => {
      const res = await axios.get<ShareListItem[]>('/share');
      return res.data ?? [];
    },
    enabled: options?.enabled ?? true,
  });
}

/**
 * Revokes a share link by its opaque, non-secret `id` (the hashed #share#
 * handle returned by GET /share).
 *
 * DELETE /share `{ id }` → revokes; `404` if the caller isn't the minter
 * (owner-gated server-side by CreatedBy). The `id` is NOT the raw token — it
 * leaks nothing, so it's safe to hold client-side and send here.
 */
export function useRevokeShare(email?: string) {
  const axios = useAxios({ email });

  return useMutation({
    defaultErrorMessage: 'Failed to revoke share link',
    mutationFn: async (props: { id: string }): Promise<void> => {
      await axios.delete('/share', { data: { id: props.id } });
    },
  });
}
